import { z } from "zod";
import { responseSchema } from "../http.js";
/**
 * ═══════════════════════════════════════════════════════════
 * La red — patrocinio, tipo de cuenta y enlaces de referido
 *
 * Esta es la fundación del plan de compensación: sin un árbol correcto no
 * hay comisión que calcular. Dos decisiones de este archivo existen para
 * corregir dos fallos MEDIDOS del sistema en producción (auditoría del
 * 25-ago-2026), y por eso están escritas aquí y no en el backend: son
 * frontera del monorepo y las dos mitades tienen que verlas igual.
 *
 * ── Fallo 1 · el patrocinador fantasma ─────────────────────
 * Producción reparte enlaces con el uid crudo de Firebase dentro
 * (`/shop/register/<uid28chars>`) y guarda ese texto sin comprobar que
 * exista nadie detrás. Resultado: un identificador que difiere en UNA
 * letra del de una persona real, y $779.80 atrapados en una cuenta que no
 * existe.
 *
 * Contra eso, tres capas:
 *   1. Un alfabeto SIN caracteres ambiguos (ver `REFERRAL_CODE_ALPHABET`).
 *   2. Un carácter de control dentro del propio código: una letra mal
 *      tecleada se detecta ANTES de tocar la base.
 *   3. Validación referencial en el servidor — la única que garantiza algo.
 *      Un código que no resuelve a un usuario real es un ERROR, nunca un
 *      silencio. Ver `network.service.ts`.
 *
 * ── Fallo 2 · el rol como efecto colateral ─────────────────
 * En producción, entrar por enlace de invitación te convierte en socio. Por
 * eso los 69 usuarios son socios, ninguno tiene un solo cliente externo,
 * nadie pasa la regla 40/60 y el bono de red de 8 niveles nunca ha pagado
 * un peso.
 *
 * Aquí `customerType` y `sponsorId` son datos INDEPENDIENTES. Traer código
 * dice de QUIÉN cuelgas; no dice QUÉ eres. Lo segundo se declara, y se
 * puede cambiar después con un acto explícito que queda registrado.
 * ═══════════════════════════════════════════════════════════
 */
// ── Tipo de cuenta en la red ─────────────────────────────
/**
 * Qué es una persona DENTRO de la red. Es ortogonal a `Role` (el permiso
 * que tiene en el sistema) y ortogonal a tener patrocinador.
 *
 * - `retail`: compra y ya. Cuenta como CLIENTE EXTERNO de su patrocinador,
 *   que es justo lo que la regla 40/60 exige y lo que hoy no existe.
 * - `preferred_customer`: cliente con paquete de recompra personal. También
 *   cuenta como cliente externo.
 * - `distributor`: hace el negocio. NO cuenta como cliente externo de nadie.
 *
 * Por qué se conservan los nombres del sistema actual: para que el día que
 * se comparen cifras viejas y nuevas, "distribuidor" signifique lo mismo en
 * las dos. Renombrarlos aquí solo aplazaría la traducción.
 */
export const CUSTOMER_TYPES = ["retail", "preferred_customer", "distributor"];
export const customerTypeSchema = z.enum(CUSTOMER_TYPES);
export const CUSTOMER_TYPE_LABELS = {
    retail: "Cliente",
    preferred_customer: "Cliente preferente",
    distributor: "Socio",
};
/**
 * El tipo con el que nace una cuenta cuando nadie dice otra cosa.
 *
 * "Por defecto, toda cuenta nace como consumidor hasta que se escale"
 * — `docs/01_MODELO_FRANQUICIA_Y_ONBOARDING.md`.
 *
 * OJO: esto es el valor que sugiere la interfaz, NO un valor por omisión
 * del contrato. `enrollRequestSchema` exige `customerType` sin default a
 * propósito: quien da de alta a alguien tiene que decir qué es. Un default
 * en el contrato es exactamente cómo producción acabó con 69 socios.
 */
export const DEFAULT_CUSTOMER_TYPE = "retail";
/**
 * ¿Esta persona cuenta como CLIENTE EXTERNO de su patrocinador?
 *
 * Es el numerador de la regla 40/60 y la razón por la que hoy nadie cobra
 * el bono de red. Un predicado con nombre, en el contrato compartido, para
 * que backend y frontend cuenten lo mismo y nadie lo reimplemente al revés.
 */
export function countsAsExternalClient(type) {
    return type !== "distributor";
}
// ── Estado del nodo ──────────────────────────────────────
/**
 * - `active`: cobra y genera.
 * - `inactive`: baja voluntaria o por inactividad. Sigue EN el árbol —ver
 *   abajo— pero no cobra; el recorrido de comisiones lo salta o lo comprime.
 * - `suspended`: bloqueado por administración.
 */
export const NODE_STATUSES = ["active", "inactive", "suspended"];
export const nodeStatusSchema = z.enum(NODE_STATUSES);
export const NODE_STATUS_LABELS = {
    active: "Activo",
    inactive: "Inactivo",
    suspended: "Suspendido",
};
// ── El código de referido ────────────────────────────────
/**
 * ═══════════════════════════════════════════════════════════
 * El código es un NÚMERO CONSECUTIVO. Decisión de Anahí (1-sep-2026):
 * «aquí en la primera línea es socio 1111-1112, 1111-1113, 1111-1114».
 *
 * Antes era aleatorio (Crockford Base32, 7 caracteres + dígito de control
 * de Luhn). Lo que se cambió, y lo que costó, está escrito aquí porque
 * quien lo lea dentro de un año va a preguntar exactamente esto:
 *
 * ── Lo que se GANA ─────────────────────────────────────────
 * Un número que se dicta por teléfono, se recuerda y se escribe sin
 * dudar. `1111-1112` es el número de socio de toda la vida en este
 * negocio; `KSSG-E6RA` es una cadena que hay que deletrear.
 *
 * ── Lo que se PIERDE, y hay que saberlo ────────────────────
 * 1. **Se puede adivinar.** Si el mío es 1111-1116, el 1111-1117 existe
 *    o existirá. Cualquiera puede recorrer los números y colgarse de la
 *    red de quien quiera. Antes, con 32⁷ ≈ 3.4 × 10¹⁰ combinaciones,
 *    acertar era imposible.
 * 2. **Se puede enumerar.** El resolvedor público de códigos devuelve el
 *    NOMBRE de quien está detrás; recorriendo los números se saca la
 *    lista de socios. La contención es el límite por IP de ese endpoint
 *    (`referral.rate-limit.ts`) — no la longitud del código.
 * 3. **Se filtra el tamaño del negocio.** El número dice cuánta gente ha
 *    entrado y en qué orden.
 * 4. **Ya no hay dígito de control.** Un número mal tecleado no se caza
 *    al escribirlo: cae en la validación referencial del servidor, que
 *    ahora es la ÚNICA defensa contra colgarse de quien no era. Por eso
 *    esa validación no se puede relajar nunca.
 * ═══════════════════════════════════════════════════════════
 */
/**
 * Los caracteres que un código puede llevar: solo dígitos.
 *
 * Se conserva la constante —y no un `/^\d+$/` suelto— porque
 * `referralCodeSchema` la usa para construir su regex y porque nombra la
 * decisión: si mañana el esquema admitiera letras otra vez, se cambia
 * aquí y todo lo demás sigue.
 */
export const REFERRAL_CODE_ALPHABET = "0123456789";
/** Ocho dígitos: `11111112` se enseña como `1111-1112`. */
export const REFERRAL_CODE_LENGTH = 8;
/**
 * El primero que se reparte. La raíz de la casa es `1111-1111` y de ahí
 * hacia arriba, uno por persona que entra a la red.
 *
 * Empieza en 1111 1111 y no en 1 por dos razones: todos los códigos miden
 * lo mismo (nada de rellenar con ceros a la izquierda, que la gente
 * escribe mal) y el 1111 de cabecera es la marca — itsthebest**1111**.
 */
export const REFERRAL_CODE_FIRST = 11_111_111;
/** El último que cabe en ocho dígitos. Después de este hay que crecer. */
export const REFERRAL_CODE_LAST = 99_999_999;
/**
 * Cuánta gente cabe con este esquema: unos 88.9 millones. No es una
 * limitación práctica, pero el número está escrito para que la respuesta
 * exista el día que alguien pregunte.
 */
export const REFERRAL_CODE_SPACE = REFERRAL_CODE_LAST - REFERRAL_CODE_FIRST + 1;
/** Cómo se le enseña a la gente: `1111-1112`. Se guarda SIN el guion. */
export function formatReferralCode(code) {
    const clean = code.toUpperCase();
    if (clean.length !== REFERRAL_CODE_LENGTH)
        return clean;
    return `${clean.slice(0, 4)}-${clean.slice(4)}`;
}
/**
 * De lo que alguien escribe (o pega, o dicta) a la forma canónica.
 *
 * Quita todo lo que no sea dígito —el guion, los espacios, el salto de
 * línea que se cuela al copiar de WhatsApp— y traduce a su dígito las tres
 * letras que la gente teclea por error al copiar un número: la `O` por el
 * cero, la `I` y la `L` por el uno.
 *
 * Ese mapeo se conserva del esquema anterior a propósito. Sigue siendo el
 * error de tecleo más común y ahora no hay dígito de control detrás que lo
 * cace: convertirlo aquí es lo único que separa a esa persona de un
 * "código no encontrado" que no sabría cómo arreglar.
 *
 * No valida nada: solo normaliza. La validación es `parseReferralCode`.
 */
export function normalizeReferralCode(raw) {
    /*
     * Se acepta el ENLACE completo, no solo el código (Anahí, 14-sep-2026:
     * «copia esto en lugar de solo el código para reasignar»). De
     * `https://…/register/1111-1134` o `…/r/11111134` se toma el último
     * tramo de la ruta; lo demás del enlace no aporta dígitos que valgan.
     */
    const trimmed = raw.trim();
    const source = /^https?:\/\/|\//.test(trimmed)
        ? (trimmed.replace(/[?#].*$/, "").replace(/\/+$/, "").split("/").pop() ?? trimmed)
        : trimmed;
    return source
        .toUpperCase()
        .replace(/O/g, "0")
        .replace(/[IL]/g, "1")
        .replace(/[^0-9]/g, "");
}
/** Por qué un código no sirve. El frontend ramifica sobre esto, no sobre el texto. */
export const REFERRAL_CODE_ERRORS = {
    EMPTY: "EMPTY",
    BAD_LENGTH: "BAD_LENGTH",
    BAD_CHARACTER: "BAD_CHARACTER",
};
export const REFERRAL_CODE_ERROR_MESSAGES = {
    EMPTY: "Escribe el código de tu patrocinador",
    BAD_LENGTH: `Un código de socio tiene ${REFERRAL_CODE_LENGTH} dígitos`,
    BAD_CHARACTER: "Un código de socio son solo números",
};
/**
 * Normaliza y comprueba la forma. Devuelve el código canónico o el motivo
 * exacto del rechazo.
 *
 * Es lo primero que corre en el registro y en el resolvedor de códigos. Un
 * código que no pasa por aquí no llega nunca a una consulta a la base.
 *
 * Ojo con lo que esta función YA NO puede hacer: sin dígito de control, un
 * número bien formado que sea de otra persona pasa por aquí sin ruido. La
 * única defensa contra colgarse de quien no era es la validación
 * referencial del servidor (`resolveReferralCode`), y por eso su mensaje
 * de error tiene que seguir diciendo de QUIÉN es el código que se resolvió
 * — para que quien se registra lo vea antes de confirmar.
 */
export function parseReferralCode(raw) {
    const fail = (error) => ({
        ok: false,
        error,
        message: REFERRAL_CODE_ERROR_MESSAGES[error],
    });
    if (raw === null || raw === undefined)
        return fail(REFERRAL_CODE_ERRORS.EMPTY);
    // Se mide el crudo, no el normalizado: si alguien manda basura entera
    // ("????????"), normalizar la borra y el mensaje sería "escribe el
    // código" cuando lo que pasa es que escribió caracteres que no usamos.
    const hadContent = raw.trim().length > 0;
    const code = normalizeReferralCode(raw);
    if (code.length === 0) {
        return fail(hadContent ? REFERRAL_CODE_ERRORS.BAD_CHARACTER : REFERRAL_CODE_ERRORS.EMPTY);
    }
    if (code.length !== REFERRAL_CODE_LENGTH)
        return fail(REFERRAL_CODE_ERRORS.BAD_LENGTH);
    return { ok: true, code };
}
export function isValidReferralCode(raw) {
    return parseReferralCode(raw).ok;
}
/**
 * El código ya guardado: canónico, sin guion, sin ambigüedades.
 * Se usa para leer de la base, no para validar lo que teclea la gente
 * (para eso está `referralCodeInputSchema`, que además normaliza).
 */
export const referralCodeSchema = z
    .string()
    .length(REFERRAL_CODE_LENGTH)
    .regex(new RegExp(`^[${REFERRAL_CODE_ALPHABET}]+$`));
/**
 * Lo que llega por el API. Normaliza ANTES de validar, así que
 * `abcd-efgh`, `ABCD EFGH` y `ABCDEFGH` son el mismo código, y el mensaje
 * de error distingue "mal escrito" de "no existe".
 */
export const referralCodeInputSchema = z
    .string()
    .transform((raw) => parseReferralCode(raw))
    .superRefine((result, ctx) => {
    if (!result.ok)
        ctx.addIssue({ code: "custom", message: result.message });
})
    .transform((result) => (result.ok ? result.code : ""));
// ── El enlace ────────────────────────────────────────────
export const REFERRAL_LINK_PARAM = "ref";
/**
 * El enlace que un socio comparte.
 *
 * Lleva el código en la RUTA, no en la query: un `?ref=` se pierde en
 * cuanto alguien reenvía el enlace desde una app que limpia parámetros, y
 * entonces la persona se registra sin patrocinador y nadie se entera hasta
 * el corte del mes.
 */
export function buildReferralLink(code, origin) {
    const clean = origin.replace(/\/+$/, "");
    /*
     * `/register/CODE` — la estructura del sistema viejo (Anahí, 27-ago:
     * "como itsthebest1111.com/shop/register/<uid>"), pero con el CÓDIGO
     * en vez del uid crudo, para no filtrar identificadores internos.
     * `/r/CODE` sigue vivo como ruta para los enlaces ya repartidos.
     *
     * Desde que el código es un número consecutivo (1-sep), este enlace
     * ya NO lleva dentro un carácter de control que cace la errata: quien
     * copie mal un dígito llega a la pantalla de registro con el código de
     * OTRA persona, bien formado. Lo único que lo detiene es que el
     * registro enseñe el NOMBRE de quien resolvió el código antes de
     * confirmar — por eso esa pantalla no puede dejar de enseñarlo.
     */
    return `${clean}/register/${code.toUpperCase()}`;
}
// ── La posición en el árbol ──────────────────────────────
/**
 * Hasta dónde paga el plan: 8 niveles el bono uninivel, 5 el de inicio
 * rápido y 5 el de franquicia. Es el techo del PLAN, no del árbol.
 */
export const PLAN_MAX_LEVELS = 8;
/**
 * Techo del ÁRBOL. No es lo mismo que el del plan: la red puede —y debe—
 * ser más profunda que los niveles que paga.
 *
 * Existe porque `ancestors` es un arreglo dentro del documento y sin tope
 * volveríamos al problema que estamos corrigiendo: un documento que crece
 * sin límite. 64 ObjectIds son ~768 bytes, muy lejos del MiB de Mongo, y
 * son más del doble del recorrido más profundo que hace el plan (los 30
 * niveles del hito del millón de dólares).
 *
 * Pasarse no se ignora: el alta FALLA. Una red de 64 de profundidad es un
 * dato corrupto o un ataque, no un caso de uso.
 */
export const MAX_NETWORK_DEPTH = 64;
/**
 * Un nodo de la red — la posición de una persona en el árbol de patrocinio.
 *
 * ── Por qué `ancestors[]` y no `referrals.levelX[]` ────────
 * El sistema actual guarda, dentro del documento de cada usuario, la lista
 * de TODOS sus referidos por nivel. Eso tiene dos techos: el documento
 * crece con la red hasta chocar con el límite de 1 MiB de Mongo/Firestore,
 * y cada alta en cualquier punto de la red reescribe el documento de todos
 * sus ancestros — escrituras calientes sobre un puñado de documentos.
 *
 * Aquí se guarda al revés. Cada quien lleva la lista de quienes tiene
 * ENCIMA, que está acotada por la profundidad y se escribe UNA vez, al
 * darse de alta. Y "toda mi red hasta 8 niveles" pasa de recorrer arreglos
 * a una sola consulta indexada:
 *
 *     { ancestors: miId, depth: { $gt: miDepth, $lte: miDepth + 8 } }
 *
 * ── Por qué es su propia colección ─────────────────────────
 * Estar en la red es un ACTO, no un efecto de haberse registrado. Separar
 * el nodo de la cuenta hace que esa distinción sea estructural: existir
 * como usuario y ocupar una posición en el árbol son dos cosas, y la
 * segunda no ocurre sola.
 */
export const networkNodeSchema = z.object({
    id: z.string(),
    userId: z.string(),
    referralCode: referralCodeSchema,
    /** El patrocinador DIRECTO, o `null` en la raíz. Es un id de usuario. */
    sponsorId: z.string().nullable(),
    /**
     * Toda la línea ascendente, de la RAÍZ hacia abajo. El último elemento es
     * el patrocinador directo — el mismo valor que `sponsorId`, duplicado a
     * propósito para que el nivel N se lea sin recorrer nada:
     *
     *     nivel 1 (directo) = ancestors[depth - 1]
     *     nivel L           = ancestors[depth - L]
     */
    ancestors: z.array(z.string()),
    /** `ancestors.length`. La raíz es 0. */
    depth: z.number().int().min(0).max(MAX_NETWORK_DEPTH),
    customerType: customerTypeSchema,
    status: nodeStatusSchema,
    enrolledAt: z.iso.datetime(),
    /**
     * Cierre de la ventana del bono de inicio rápido. Se congela al alta: si
     * dependiera de un cálculo en tiempo de lectura, cambiar la configuración
     * movería retroactivamente quién cobró qué.
     */
    firstMonthEndAt: z.iso.datetime(),
});
/** Nivel al que está `ancestorUserId` respecto del nodo. `null` si no está encima. */
export function levelOfAncestor(node, ancestorUserId) {
    const index = node.ancestors.lastIndexOf(ancestorUserId);
    if (index < 0)
        return null;
    return node.ancestors.length - index;
}
// ── Alta en la red ───────────────────────────────────────
/**
 * Lo que hace falta para ocupar una posición.
 *
 * `customerType` va sin `.default()` A PROPÓSITO. Es el corazón del arreglo
 * del fallo 2: si tuviera valor por omisión, el alta volvería a decidir
 * sola qué es la gente, que es exactamente cómo el sistema actual acabó con
 * 69 socios y cero clientes. Aquí hay que decirlo. La interfaz sugiere
 * `DEFAULT_CUSTOMER_TYPE`; el contrato no sugiere nada.
 *
 * `referralCode` es opcional —hay altas sin patrocinador, empezando por la
 * raíz— pero NO es opcional que resuelva: si viene y no corresponde a
 * nadie, el alta falla. Nunca se guarda un patrocinador que no existe.
 */
export const enrollRequestSchema = z.object({
    userId: z.string().min(1),
    customerType: customerTypeSchema,
    referralCode: referralCodeInputSchema.optional(),
});
/**
 * Cambiar de tipo es un ACTO REGISTRADO, no un `update`.
 *
 * Quién lo hizo, cuándo y por qué. El día que alguien pregunte por qué esta
 * persona dejó de contar como cliente externo de su patrocinador —y con
 * ello por qué el patrocinador dejó de pasar la regla 40/60— la respuesta
 * tiene que estar escrita, no deducirse.
 */
export const customerTypeChangeSchema = z.object({
    from: customerTypeSchema,
    to: customerTypeSchema,
    at: z.iso.datetime(),
    byUserId: z.string(),
    reason: z.string().min(1).max(500),
});
export const changeCustomerTypeRequestSchema = z.object({
    customerType: customerTypeSchema,
    reason: z.string().trim().min(1, "Escribe por qué cambia").max(500),
});
/**
 * Mover una rama de patrocinador — la operación que arregla un
 * patrocinador mal asignado.
 *
 * Existe por el caso real de producción: $779.80 colgando de un
 * identificador que difiere en una letra del de una persona real. Cuando
 * eso pasa hay que poder reasignar la rama, y hacerlo de una forma que no
 * pueda crear un ciclo ni desbordar la profundidad. Ver `reassignSponsor`.
 */
export const reassignSponsorRequestSchema = z.object({
    /** El código o el ENLACE completo de invitación (14-sep-2026). */
    newSponsorCode: referralCodeInputSchema.nullable(),
    reason: z.string().trim().min(1, "Escribe por qué se reasigna").max(500),
});
/**
 * Lo que pasó con las comisiones al mover la rama (14-sep-2026): las
 * pendientes se borran y se recalculan con la línea nueva; lo pagado no
 * se toca. `null` si el recálculo falló (el movimiento sí quedó).
 */
export const branchRecalcSchema = z.object({
    users: z.number().int().min(0),
    orders: z.number().int().min(0),
    removed: z.number().int().min(0),
    fastStartInserted: z.number().int().min(0),
    cutsRerun: z.array(z.string()),
});
// ── Lo que se responde ───────────────────────────────────
/**
 * Lo que se sabe de un código SIN sesión, para pintar "te invita X" en la
 * pantalla de registro.
 *
 * Deliberadamente pobre: nombre de pila e inicial del apellido. El
 * resolvedor es público y sin sesión —tiene que serlo, se usa antes de
 * existir la cuenta—, así que devuelve lo justo para que quien se registra
 * reconozca a quien lo invitó y nada que sirva para hacer un censo.
 */
export const referralSponsorSchema = z.object({
    referralCode: referralCodeSchema,
    displayName: z.string(),
    customerType: customerTypeSchema,
});
export const networkDownlineQuerySchema = z.object({
    /** Hasta qué nivel. Por omisión, los que paga el plan. */
    levels: z.coerce.number().int().min(1).max(MAX_NETWORK_DEPTH).default(PLAN_MAX_LEVELS),
    customerType: customerTypeSchema.optional(),
    page: z.coerce.number().int().min(1).default(1),
    perPage: z.coerce.number().int().min(1).max(200).default(50),
});
export const downlineMemberSchema = z.object({
    userId: z.string(),
    displayName: z.string(),
    referralCode: referralCodeSchema,
    level: z.number().int().min(1),
    /** Quién lo invitó (userId del patrocinador) — con esto la pantalla
     * arma el ÁRBOL de la red, como los invitados del sistema viejo. */
    sponsorId: z.string().nullable(),
    customerType: customerTypeSchema,
    status: nodeStatusSchema,
    enrolledAt: z.iso.datetime(),
});
export const downlineSchema = z.object({
    items: z.array(downlineMemberSchema),
    total: z.number().int().min(0),
    page: z.number().int().min(1),
    perPage: z.number().int().min(1),
    /** Cuántos hay en cada nivel — el resumen que pide el tablero de red. */
    countsByLevel: z.record(z.string(), z.number().int().min(0)),
});
/**
 * Una fila del UNILEVEL del panel (Anahí, 27-ago): el nodo con los
 * datos de su persona ya unidos — el panel pinta el árbol completo y no
 * puede hacer una petición por nombre.
 */
export const adminNetworkItemSchema = z.object({
    userId: z.string(),
    displayName: z.string(),
    email: z.string(),
    referralCode: referralCodeSchema,
    sponsorId: z.string().nullable(),
    depth: z.number().int().min(0),
    customerType: customerTypeSchema,
    status: nodeStatusSchema,
    enrolledAt: z.iso.datetime(),
    /** Invitados DIRECTOS (nivel 1). Lo cuenta el servidor. */
    directCount: z.number().int().min(0),
});
export const adminNetworkListResponseSchema = responseSchema(z.object({ items: z.array(adminNetworkItemSchema) }));
export const resolveReferralResponseSchema = responseSchema(z.object({ sponsor: referralSponsorSchema }));
export const networkNodeResponseSchema = responseSchema(z.object({ node: networkNodeSchema }));
export const downlineResponseSchema = responseSchema(downlineSchema);
/**
 * Una compra de alguien de mi red, con lo que me generó a MÍ.
 *
 * `commissionCents` puede ser 0 con motivo: una compra que no pagó nada
 * —porque el bono solo cubre la primera, porque el comprador es cliente,
 * porque el nivel queda fuera del gradiente— tiene que poder decir POR
 * QUÉ. Un cero mudo se lee como un error del sistema.
 */
export const memberPurchaseSchema = z.object({
    orderId: z.string(),
    folio: z.string(),
    /** "producto" o "paquete": qué clase de compra fue. */
    kind: z.enum(["producto", "paquete", "franquicia"]),
    concept: z.string(),
    amountCents: z.number().int().min(0),
    volumePoints: z.number().int().min(0),
    paidAt: z.iso.datetime(),
    /** Lo que ESA compra le generó a quien pregunta. */
    commissionCents: z.number().int().min(0),
    /** Con qué bono: BIR, BUR… `null` si no generó nada. */
    bonusType: z.string().nullable(),
    /** A qué nivel de su línea cobró. */
    level: z.number().int().min(1).nullable(),
    /** El estado de esa comisión: pendiente, pagada… */
    commissionStatus: z.string().nullable(),
});
export const memberActivityResponseSchema = responseSchema(z.object({
    member: z.object({ userId: z.string(), displayName: z.string(), level: z.number().int() }),
    purchases: z.array(memberPurchaseSchema),
    totals: z.object({
        amountCents: z.number().int().min(0),
        volumePoints: z.number().int().min(0),
        commissionCents: z.number().int().min(0),
    }),
}));
export const NETWORK_ROUTES = {
    /** Público y sin sesión: la pantalla de registro lo llama antes de existir la cuenta. */
    resolveCode: (code) => `/api/network/referral/${code}`,
    /** El nodo de quien pide, con su código y su enlace. */
    me: "/api/network/me",
    /** Alta en la red. */
    enroll: "/api/network/enroll",
    downline: "/api/network/downline",
    /**
     * ── Qué ha comprado alguien de MI red, y qué me generó ─────────────
     * (Anahí, 29-ago): "como socio o franquiciatario deben poder ver las
     * comisiones y puntos que ha generado cada una de las personas que han
     * invitado [...] producto o paquete, monto, puntos y comisión, y el
     * tipo de comisión — si es BIR, si es BUR".
     *
     * Devuelve DOS cosas atadas: lo que esa persona compró, y de esas
     * compras, lo que le tocó a QUIEN PREGUNTA. Nunca lo que cobraron
     * otros: el bono de un mismo pedido se reparte entre cinco niveles, y
     * enseñar la línea entera sería contarle a cada quien lo que ganan sus
     * compañeros.
     */
    memberActivity: (userId) => `/api/network/downline/${userId}/actividad`,
    /** El unilevel completo del panel. */
    adminList: "/api/admin/network",
    adminNode: (userId) => `/api/admin/network/${userId}`,
    adminCustomerType: (userId) => `/api/admin/network/${userId}/customer-type`,
    adminReassign: (userId) => `/api/admin/network/${userId}/sponsor`,
};
/** La respuesta de reasignar: el nodo movido y qué pasó con sus comisiones. */
export const reassignSponsorResponseSchema = responseSchema(z.object({ node: networkNodeSchema, recalculated: branchRecalcSchema.nullable() }));
//# sourceMappingURL=network.js.map